import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, buttonVariants } from "../../components/ui/button";
import BackIcon from "../../components/back-icon";
import { ArrowRight, CircleUserRound, Loader2, TrashIcon } from "lucide-react";
import { useDoctor } from "../../lib/hooks/doctors/use-doctor";
import { useBookAppointment } from "../../lib/hooks/doctors/use-book-appointment";
import { useAppointmentData } from "../../contexts/appointment-data-context";
import { format } from "date-fns";
import {
  BookAppointmentSchema,
  bookAppointmentSchema,
} from "../../lib/validation/book-appointment";
import { useToast } from "../../components/ui/use-toast";
import { getBase64FromFile, getDateFromSlotTime } from "../../lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "../../components/ui/drawer";
import { useEffect, useState } from "react";
import { useAuth } from "../../contexts/auth-context";
import { PrescriptionIcon } from "./prescriptions";
import { usePatientsByMobile } from "../../lib/hooks/doctors/use-patients-by-mobile";
import Logo from "../../components/logo";

interface LargeConfirmAppointmentProps {
  onAddHealthRecords: () => void; // Callback function triggered when the button is clicked
}


const LargeConfirmAppointment: React.FC<LargeConfirmAppointmentProps> = ({
  onAddHealthRecords,
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { toast } = useToast();
  const [confirmDrawerOpen, setConfirmDrawerOpen] = useState(false);

  const params = useParams();
  const professionalId = params.professionalId;
  const { doctor, isLoading } = useDoctor(professionalId);

  const { fetched: authFetched, mobile } = useAuth();

  const { data, setData } = useAppointmentData();

  // console.log("health records api data : ",data)

  const { patients, isFetched } = usePatientsByMobile({ enabled: true });

  const { bookAppointment, isPending } = useBookAppointment();

  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 1024);


  useEffect(() => {
    const handleResize = () => setIsLargeScreen(window.innerWidth > 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  async function handleBookAppointment() {
    const recordsWithBase64 = await Promise.all(
      (data?.healthRecords ?? []).map(async (record) => {
        return {
          reportData: await getBase64FromFile(record),
          reportName: record.name,
        };
      })
    );
    const [startSlot, endSlot] = data?.slotTiming?.split(" - ") ?? ["", ""];
    const startTime = getDateFromSlotTime(data?.slotDate, startSlot);
    const endTime = getDateFromSlotTime(data?.slotDate, endSlot);
    const payload: BookAppointmentSchema = {
      patientId: data?.patient?.id ?? 0,
      appointmentType: "WALK_IN",
      appointmentExists: false,
      appointmentDetails: {
        doctorName: doctor?.fullName ?? "",
        appointmentDate: data?.slotDate ?? new Date(),
        startTime,
        endTime,
        fee: doctor?.fee == null ? undefined : doctor.fee.toString(),
        professionalId: doctor?.professionalId.toString() ?? "",
        branchId: Number(data?.branchDetails?.[0].branchId) ?? 0,
      },
      symptoms: data?.symptoms ?? [],
      healthRecords: [
        {
          doctorNameOrLabName: data?.healthRecordPhysician,
          uploadRecords: recordsWithBase64.map((record) => ({
            reportData: record.reportData,
            reportName: record.reportName,
          })),
        },
      ],
    };
    const parsedData = bookAppointmentSchema.safeParse(payload);
    if (parsedData.error)
      return toast({
        title: "Invalid data",
        description: parsedData.error.errors
          .map((err) => `${err.message} in ${err.path.join(".")}`)
          .join(", "),
        variant: "destructive",
      });
    if (parsedData.success) {
      bookAppointment(parsedData.data, {
        onSuccess: (newData) => {
          if (data?.patient?.id) {
            localStorage.setItem("selectedPatientId", data.patient.id.toString());
            queryClient.invalidateQueries({ queryKey: ["appointments", data.patient.id] });
          }
          setConfirmDrawerOpen(false);
          document.body.style.position = "";
          setData(null);
          navigate(`/appointment/${newData?.bookingId}`);
        },
      });
    }
  }

  useEffect(() => {
    if (isFetched && !patients?.length && professionalId)
      navigate(
        `/add-patient?redirectUrl=/doctors/${professionalId}/appointment/confirm?backUrl=/doctors/${professionalId}/appointment`,
      );
  }, [patients, isFetched, professionalId, navigate]);

  useEffect(() => {
    if (!mobile && authFetched)
      navigate(
        `/verify-patient?redirect=/doctors/${professionalId}/appointment/confirm`,
      );

    if (
      isFetched &&
      patients?.length &&
      !isLoading &&
      !data?.symptoms?.length &&
      !data?.patient?.id
    )
      navigate(`/doctors/${doctor?.professionalId}/appointment`);
  }, [
    data,
    doctor?.professionalId,
    navigate,
    isLoading,
    mobile,
    authFetched,
    professionalId,
    isFetched,
    patients?.length,
  ]);

  const [deletePopupOpen, setDeletePopupOpen] = useState(false);


  function handleDeleteHealthRecords() {
    setData({
      ...data,
      healthRecordPhysician: undefined,
      healthRecords: [],
      branchDetails: data?.branchDetails ?? [],
    });
    setDeletePopupOpen(false);
  }
  const branch = data?.branchDetails?.[0];
  // console.log("branch : ", branch);

  return (
    <>
      <div className="max-w-2xl w-full  pb-10 pl-3 pr-3 space-y-6 bg-white rounded-xl shadow-lg border border-gray-300">
        {/* Header Section */}
        <div className="flex items-center gap-6">
          {/* <Link
            to={`/doctors/${doctor?.professionalId}?step=3`}
            className={`${buttonVariants({
              variant: "ghost",
              size: "icon",
            })} relative group`}
          >
            <BackIcon />
            <span className="absolute top-full left-1/2 -translate-x-1/2 mt-1 hidden group-hover:flex items-center p-1 rounded text-xs text-white bg-gray-800 shadow-lg">
              Back
            </span>
          </Link> */}

        </div>
        {/* Appointment Details */}
        <div className="space-y-6 border-b pb-6">
          {data?.slotDate && data.dayOfWeek && data.slotTiming && (
            <div className="text-center">
              <p className="text-gray-600 bold text-2xl pb-1">Appointment Date</p>
              <p className="text-lg font-bold text-primary">
                {/* {format(data.slotDate, "d MMM yyyy")}, {data.dayOfWeek},{" "}
                {data.slotTiming} */}
                {/* {  */}
                {data.slotTiming},{" " }{data.dayOfWeek},{" "} {format(data.slotDate, "d MMM yyyy")}
              </p>
            </div>
          )}

          <div>
            <span className="text-lg font-bold text-gray-800">For</span> -
            <span className="text-lg font-bold text-gray-800">     {data?.patient?.name}</span>
            <p className="text-sm text-gray-500">
              symptoms - {data?.symptoms?.join(", ") ?? "No symptoms provided"}
            </p>
          </div>

          <div>
            <span className="text-lg font-bold text-gray-800">At</span> -
            <span className="text-lg font-bold text-gray-800">   {branch?.clinicName}</span>
            {(branch?.addressLine1 || branch?.addressLine2 || branch?.subDistrict || branch?.state || branch?.pincode) && (
              <p className="text-sm text-gray-500">
                {[branch?.addressLine1, branch?.addressLine2].filter(Boolean).join(", ")}
                {branch?.subDistrict ? `, ${branch.subDistrict}` : ""}
                {branch?.state ? `, ${branch.state}` : ""}
                {branch?.pincode ? ` - ${branch.pincode}` : ""}
              </p>
            )}

          </div>
        </div>

        {/* Health Records Section */}
        <div className="space-y-3">
          {!data?.healthRecords?.length ? (
            <Button variant="outline" onClick={onAddHealthRecords}>
              Upload health records
            </Button>
          ) : (
            <div className="flex items-center gap-2  border rounded-lg bg-gray-50">
              <PrescriptionIcon />
              <div className="flex-1">
                <h4 className="text-sm font-bold text-gray-700">
                  {data.healthRecords.map((record) => record.name).join(", ")}
                </h4>
                {data?.healthRecordPhysician && (
                  <p className="text-sm text-gray-600">
                    Consulted with {data.healthRecordPhysician}
                  </p>
                )}
              </div>
              <button
                onClick={() => setDeletePopupOpen(true)}
                className="text-sm text-red-600 hover:text-red-800"
              >
                <TrashIcon className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        {/* Confirmation Popup */}
        {deletePopupOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-[90%] max-w-md">
              <h2 className="text-lg font-bold mb-4">Confirm Delete</h2>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to delete the health records?
              </p>
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setDeletePopupOpen(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteHealthRecords}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirm Button */}
        <div className="flex justify-center">
          <Drawer open={confirmDrawerOpen} onOpenChange={setConfirmDrawerOpen}>
            <DrawerTrigger asChild>
              <Button className="w-full max-w-sm py-2 font-bold shadow-lg">
                Book appointment
              </Button>
            </DrawerTrigger>
            <DrawerContent className="max-w-xl mx-auto p-6">
              <DrawerHeader>
                <DrawerTitle className="text-center text-xl font-bold">
                  Service and Liability Waiver Agreement
                </DrawerTitle>
              </DrawerHeader>
              <div className="py-4 text-sm text-gray-700">
                <p>
                  I hereby confirm, acknowledge and consent that SwingBell Labs,
                  the developer of Asimith EHR, is only a facilitator of the
                  service being provided by the doctors, and there is no
                  guarantee to the quality, delivery, or efficacy of the
                  service being provided by the doctors. I permanently disclaim
                  and hold SwingBell Labs harmless from any future claims or
                  demands against them in respect of the services provided by
                  the doctors.
                </p>
              </div>
              <DrawerFooter>
                <Button
                  onClick={handleBookAppointment}
                  className="w-full flex items-center justify-center gap-2 py-3 font-bold shadow-md"
                  disabled={isPending}
                >
                  {isPending && <Loader2 className="w-5 h-5 animate-spin" />}
                  <span>I Agree, Proceed</span>
                </Button>
              </DrawerFooter>
            </DrawerContent>
          </Drawer>
        </div>
      </div>
    </>
  );
}

export default LargeConfirmAppointment;

